var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
/**
 * Copyright 2019, SumUp Ltd.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useState, useEffect } from 'react';
import { css } from '@emotion/react';
import { TrackingElement } from '@sumup/collector';
import { isEmpty } from '../../../../util/helpers';
import styled from '../../../../styles/styled';
import { useClickEvent } from '../../../../hooks/useClickEvent';
import { hasSelectedChild, getIcon } from '../../SidebarService';
import { SubNavList } from '../SubNavList';
import { NavLabel } from '../NavLabel';
import { AccessibilityError } from '../../../../util/errors';
const baseStyles = ({ theme }) => css `
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  height: auto;
  width: calc(100% - (2 * ${theme.spacings.mega}));
  margin: ${theme.spacings.mega};
  padding: ${theme.spacings.bit};
  cursor: pointer;
  color: ${theme.colors.n300};
  transition: color ${theme.transitions.default};
  font: inherit;
  background: none;
  border: 0;
  outline: none;
`;
const hoverStyles = ({ theme, disabled, selected, }) => !disabled &&
    !selected &&
    css `
    &:hover,
    &:focus {
      color: ${theme.colors.n100};
    }
  `;
const selectedStyles = ({ theme, selected }) => selected &&
    css `
    color: ${theme.colors.white};
  `;
const disabledStyles = ({ theme, disabled }) => disabled &&
    css `
    cursor: not-allowed;
    color: ${theme.colors.n500};
  `;
const AggregatorContainer = styled('button')(baseStyles, hoverStyles, selectedStyles, disabledStyles);
const TRACKING_ELEMENT = 'aggregator';
export function Aggregator(_a) {
    var { children, label, defaultIcon, selectedIcon, disabled, onClick, tracking } = _a, props = __rest(_a, ["children", "label", "defaultIcon", "selectedIcon", "disabled", "onClick", "tracking"]);
    if (process.env.NODE_ENV !== 'production' &&
        process.env.NODE_ENV !== 'test' &&
        !label) {
        throw new AccessibilityError('Aggregator', 'The `label` prop is missing.');
    }
    const [isOpen, setIsOpen] = useState(false);
    const selectedChild = hasSelectedChild(children);
    const baseHandleClick = (event) => {
        if (onClick) {
            onClick(event);
        }
        if (selectedChild) {
            return;
        }
        setIsOpen((open) => !open);
    };
    const handleClick = useClickEvent(baseHandleClick, tracking, 'sidebar-nav-aggregator');
    useEffect(() => {
        setIsOpen(selectedChild);
    }, [selectedChild]);
    const icon = getIcon({
        selected: selectedChild,
        selectedIcon,
        defaultIcon,
        disabled,
    });
    return (_jsxs("li", { children: [_jsxs(AggregatorContainer, Object.assign({ selected: selectedChild, disabled: disabled, "aria-disabled": disabled, onClick: handleClick }, props, { children: [icon, _jsx(NavLabel, Object.assign({ secondary: false }, { children: label }))] })), !isEmpty(children) && !disabled && (_jsx(TrackingElement, Object.assign({ name: TRACKING_ELEMENT }, { children: _jsx(SubNavList, Object.assign({ visible: isOpen }, { children: children })) })))] }));
}
